import { useCallback, useMemo, useState } from 'react';
import type { EntitySuggestion } from '@bliks-io/contracts';
import { useWithdrawEntitySuggestion } from '@/api/mutations/useWithdrawEntitySuggestion';

export interface CreatedSuggestionSession {
  /** Docs created (eagerly POSTed) within this session, in creation order. */
  createdSuggestions: EntitySuggestion[];
  /** Record a suggestion this session created — withdrawable on cancel. */
  track: (suggestion: EntitySuggestion) => void;
  /**
   * Fire the guarded withdraw for every tracked doc the given ids do NOT
   * reference, then drop those docs from the ledger. The referenced ones stay
   * tracked (and keep naming their rows). Fire-and-forget: the backend deletes
   * only what is still pending and unreferenced, so a doc that is referenced
   * somewhere the caller can't see survives as a no-op.
   *
   * TIMING IS THE CONTRACT. That backend guard reads the CURRENT refs, so the
   * caller must not fire this while a write that un-persists the ref is still
   * in flight: the guard would see the doomed ref, answer `withdrawn: false`,
   * and the ledger would drop the obligation anyway — a permanent orphan. Call
   * it either when nothing was persisted for the doc yet (a cancelled session)
   * or once the un-persisting write has settled (the next session's start).
   */
  withdrawUnreferenced: (referencedIds: string[]) => void;
  /** `withdrawUnreferenced` with nothing referenced: withdraw all, empty the ledger. */
  withdrawAll: () => void;
  /** Empty the ledger without withdrawing (refs persisted — keep the docs). */
  clear: () => void;
  /**
   * Merge the tracked docs into an existing suggestion list for proposed-name
   * lookups (created docs aren't on any server list until a ref persists).
   * Dedupes by `_id` so a list that already contains a tracked doc (e.g. one
   * merged upstream by the flow owner) isn't doubled.
   */
  mergeInto: (existing: EntitySuggestion[] | undefined) => EntitySuggestion[] | undefined;
}

/**
 * Single ledger for suggestions created eagerly within one UI session (a
 * reassign dialog, or a whole reassign flow with its own downstream confirm).
 *
 * Ownership rule: the OUTERMOST scope that can still cancel the suggestion
 * instantiates the session exactly once and hands it down — so each created
 * suggestion has one ledger entry and one withdraw site, never two ledgers
 * racing each other's withdraws.
 */
export function useCreatedSuggestionSession(): CreatedSuggestionSession {
  const [createdSuggestions, setCreatedSuggestions] = useState<EntitySuggestion[]>([]);
  const { mutate: withdrawSuggestion } = useWithdrawEntitySuggestion();

  const track = useCallback((suggestion: EntitySuggestion) => {
    setCreatedSuggestions((prev) => [...prev, suggestion]);
  }, []);

  const withdrawUnreferenced = useCallback(
    (referencedIds: string[]) => {
      const referenced = new Set(referencedIds);
      const unreferenced = createdSuggestions.filter((s) => !referenced.has(s._id));
      if (unreferenced.length === 0) return;
      for (const suggestion of unreferenced) {
        withdrawSuggestion({ suggestionId: suggestion._id });
      }
      setCreatedSuggestions((prev) => prev.filter((s) => referenced.has(s._id)));
    },
    [createdSuggestions, withdrawSuggestion]
  );

  const withdrawAll = useCallback(() => withdrawUnreferenced([]), [withdrawUnreferenced]);

  // Bail out on an already-empty ledger so render-phase callers (the dialog's
  // reset-on-open) don't trigger a pointless extra render.
  const clear = useCallback(() => {
    setCreatedSuggestions((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const mergeInto = useCallback(
    (existing: EntitySuggestion[] | undefined) => {
      if (createdSuggestions.length === 0) return existing;
      const existingIds = new Set((existing ?? []).map((s) => s._id));
      return [...(existing ?? []), ...createdSuggestions.filter((s) => !existingIds.has(s._id))];
    },
    [createdSuggestions]
  );

  return useMemo(
    () => ({ createdSuggestions, track, withdrawUnreferenced, withdrawAll, clear, mergeInto }),
    [createdSuggestions, track, withdrawUnreferenced, withdrawAll, clear, mergeInto]
  );
}
